import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Home from './components/Home';
import Footer from './components/Footer';
import Preloader from './components/Preloader';
import Courses from "./components/Courses";
import CourseModal from "./components/CourseModal";
import Contact from "./components/Contact";
import ImageSlider from "./components/ImageSlider";
import About from "./components/About";
import { FaArrowUp } from 'react-icons/fa';

function App() {
  const location = useLocation();
  const isCourseModal = location.pathname.includes('/courses/');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('home');
  
  const handleLoadingComplete = () => {
    setLoading(false);
  };
  
  // Scroll to top button and section detection functionality
  useEffect(() => {
    const handleScroll = () => {
      // Show/hide scroll to top button
      if (window.scrollY > 500) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
      
      // Detect active section for navbar highlighting
      const sections = document.querySelectorAll('section[id]');
      const scrollPosition = window.scrollY + 100;
      
      sections.forEach(section => {
        const sectionTop = section.offsetTop;
        const sectionHeight = section.offsetHeight;
        const sectionId = section.getAttribute('id');
        
        if (scrollPosition >= sectionTop && scrollPosition < sectionTop + sectionHeight) {
          setActiveSection(sectionId);
        }
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Land on the right section when returning from a course detail page
  useEffect(() => {
    const target = location.state && location.state.scrollTo;
    if (loading || location.pathname !== '/' || !target) return;

    // Strip the intent from the history entry so a reload does not replay it.
    // Done with the raw history API on purpose: navigate() would re-render and
    // cancel the scroll we are about to schedule.
    if (window.history.state && window.history.state.usr) {
      window.history.replaceState({ ...window.history.state, usr: null }, '');
    }

    // The home page mounts empty, so wait until it is tall enough for the
    // target section to actually be reachable before scrolling.
    let timer;
    const startedAt = Date.now();

    const tryScroll = () => {
      const section = document.getElementById(target);
      const top = section ? section.offsetTop - 80 : 0;
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;

      if (section && maxScroll >= top - 4) {
        window.scrollTo({ top, behavior: 'smooth' });
        setActiveSection(target);
        return;
      }

      if (Date.now() - startedAt < 2000) timer = setTimeout(tryScroll, 60);
    };

    timer = setTimeout(tryScroll, 60);
    return () => clearTimeout(timer);
  }, [location, loading]);

  // Scroll to section functionality
  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const offsetTop = section.offsetTop;
      window.scrollTo({
        top: offsetTop - 80, // Adjust for navbar height
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };
  
  // Scroll to top functionality
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative min-h-screen bg-surface-base text-ink-muted">
      {loading ? (
        <Preloader onLoadingComplete={handleLoadingComplete} />
      ) : (
        <>
          <Navbar activeSection={activeSection} scrollToSection={scrollToSection} />
          
          <div>
            <Routes>
              <Route path="/" element={
                <div className="overflow-hidden">
                  {/* Hero + stats */}
                  <section id="home" className="relative">
                    <Home />
                  </section>

                  {/* Campus life */}
                  <section id="slider" className="section">
                    <div className="container-page">
                      <div className="max-w-2xl">
                        <span className="eyebrow">Campus Life</span>
                        <h2 className="section-title">Inside AC Tech Institute</h2>
                      </div>
                      <div className="mt-12">
                        <ImageSlider />
                      </div>
                    </div>
                  </section>

                  {/* About */}
                  <section id="about" className="section border-t border-surface-line">
                    <About />
                  </section>

                  {/* Courses */}
                  <section id="courses" className="section border-t border-surface-line bg-surface-sunken">
                    <Courses />
                  </section>

                  {/* Contact */}
                  <section id="contact" className="section border-t border-surface-line">
                    <Contact />
                  </section>
                </div>
              } />
              <Route path="/courses/:id" element={<CourseModal />} />
            </Routes>
          </div>
          
          {!isCourseModal && <Footer />}
          
          {/* Scroll to top */}
          <button
            id="scroll-to-top"
            onClick={scrollToTop}
            className={`fixed bottom-8 right-8 z-50 flex h-11 w-11 items-center justify-center rounded-full border border-surface-line bg-surface-raised text-ink-muted shadow-lg shadow-black/40 transition-all duration-300 hover:border-brand-500/60 hover:text-ink ${
              showScrollButton ? 'translate-y-0 opacity-100' : 'pointer-events-none translate-y-2 opacity-0'
            }`}
            aria-label="Scroll to top"
          >
            <FaArrowUp className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}



export default App;
